// --- src/data/m3/Unit1.js ---

export const M3_UNIT_1 = {
  id: "u1-clothes-accessories",
  title: "Unit 1: Clothes & Accessories",
  topic: "Clothing vocabulary + colors + sizes + shopping",
  iconType: "shirt",
  grammarImage: "/m3-u1-clothes.jpg",
  grammarText: "Unit focus: clothing vocabulary + colors + sizes + adjective order (opinion before fact) + 'too' and 'enough' + shopping phrases (try on, fit, pay).",
  sets: [

    // ===================================================================
    // SET 1: BASIC CLOTHING VOCABULARY (A1+)
    // ===================================================================
    {
      id: "m3-u1-set1",
      title: "Set 1: Getting Ready to Shop",
      context: `*Sábado por la mañana - Casa de María*

María abre su armario. Hoy va de compras con su amiga Laura. 
"Necesito ropa nueva para la boda de mi prima," piensa. 

*Saturday morning - Maria's house*

Maria opens her closet. Today she's going shopping with her friend Laura.
"I need new clothes for my cousin's wedding," she thinks.`,
      
      questions: [
        {
          type: "match",
          q: "Match the clothing item to where you wear it:",
          pairs: [
            { left: "Hat", right: "On your head" },
            { left: "Shoes", right: "On your feet" },
            { left: "T-shirt", right: "On your body" }
          ],
          a: "completed",
          successTip: "HEAD-FEET-BODY! Basic clothing locations. UK: 'trainers' = US: 'sneakers'! 👒👟👕"
        },
        {
          type: "mc",
          q: "Maria puts on a white _____. It's comfortable for walking in a hot day.",
          options: ["t-shirt", "coat", "scarf"],
          a: "t-shirt",
          tip: "T-shirt = casual, comfortable clothing.",
          successTip: "✓ wear = put on! (vestir) 🧤"
        },
        {
          type: "order",
          q: "Traduce: 'Yo visto pantalones de mezclilla azules.'",
          words: ["I", "wear", "blue", "jeans."],
          a: "I wear blue jeans.",
          extra: ["a", "the"],
          tip: "Color goes BEFORE noun: blue jeans (not jeans blue)."
        },
        {
          type: "mc",
          q: "In winter, I wear black _____. They are warm.",
          options: ["jeans", "sandals", "shorts"],
          a: "jeans",
          tip: "WINTER CLOTHES! Jeans = warm. Sandals/shorts = summer! ❄️☀️"
        },
        {
          type: "input",
          q: "I usually wear a red _____ when it rains.(impermeable)",
          a: "raincoat",
          alts: ["rain coat"],
          tip: "Raincoat = abrigo impermeable."
        },
        {
          type: "mc",
          q: "What color are your shoes? Mine are _____.",
          options: ["white", "whites", "a white"],
          a: "white",
          tip: "Color without article: My shoes are white."
        },
        {
          type: "match",
          q: "Match the accessory to where you wear it:",
          pairs: [
            { left: "Scarf", right: "Around neck" },
            { left: "Belt", right: "Around waist" },
            { left: "Gloves", right: "On hands" }
          ],
          a: "completed",
          successTip: "ACCESSORIES! UK: 'scarf' (plural: scarves). US same! 🧣👔🧤"
        },
        {
          type: "input",
          q: "I wear a _____ to work, normally with a red tie. It's very formal.",
          a: "suit",
          tip: "Suit = jacket + pants/skirt (formal work clothing)."
        },
        {
          type: "mc",
          q: "What color are your _____? Mine are green!",
          options: ["socks", "sock", "a sock"],
          a: "socks",
          tip: "Plural! You wear TWO socks → socks."
        },
        {
          type: "order",
          q: "Traduce: 'Ella lleva un vestido rojo.'",
          words: ["She", "wears", "a", "red", "dress."],
          a: "She wears a red dress.",
          extra: ["blue", "the"],
          successTip: "READY! Article + color + noun: a red dress. Let's go shopping! 🛍️"
        }
      ]
    },

    // ===================================================================
    // SET 2: COLORS & DESCRIPTIONS (A1+)
    // ===================================================================
    {
      id: "m3-u1-set2",
      title: "Set 2: Colors Everywhere",
      context: `*Centro Comercial*

María y Laura entran a la primera tienda. Hay ropa de todos los colores.
"¡Mira ese vestido azul!" -dice Laura. "Y esa blusa amarilla es hermosa."

*Shopping Mall*

Maria and Laura enter the first store. There are clothes in every color.
"Look at that blue dress!" -says Laura. "And that yellow blouse is beautiful."`,
      
      questions: [
        {
          type: "match",
          q: "Match the color to a common example:",
          pairs: [
            { left: "Red", right: "Strawberry color" },
            { left: "Blue", right: "Sky color" },
            { left: "Green", right: "Grass color" }
          ],
          a: "completed",
          successTip: "VISUAL MEMORY! Red=🍓, Blue=☁️, Green=🌿. Easy to remember!"
        },
        {
          type: "mc",
          q: "My shirt is _____. It's the color of the sun.",
          options: ["yellow", "black", "purple"],
          a: "yellow",
          tip: "Yellow = color of the sun ☀️."
        },
        {
          type: "order",
          q: "Traduce: 'Tengo un sombrero rosado.'",
          words: ["I", "have", "a", "pink", "hat."],
          a: "I have a pink hat.",
          tip: "Article + color + noun."
        },
        {
          type: "input",
          q: "This dress is _____. It's the color of oranges.",
          a: "orange",
          tip: "Orange = fruit AND color! 🍊"
        },
        {
          type: "mc",
          q: "I have _____ pants. They are very dark.",
          options: ["black", "white", "pink"],
          a: "black",
          successTip: "OPPOSITES! Black = ⚫ very dark, White = ⚪ very light!"
        },
        {
          type: "input",
          q: "Her scarf is _____. It's the color of grapes.",
          a: "purple",
          tip: "Purple = color of grapes 🍇."
        },
        {
          type: "match",
          q: "Match the adjective to its opposite:",
          pairs: [
            { left: "Old", right: "New" },
            { left: "Long", right: "Short" },
            { left: "Expensive", right: "Cheap" }
          ],
          a: "completed",
          successTip: "OPPOSITES RULE! Aprende siempre los opuestos! 🔄"
        },
        {
          type: "mc",
          q: "This jacket is _____ and beautiful!",
          options: ["new", "ugly", "broken"],
          a: "new",
          successTip: "Usa 'and' para unir dos adjetivos! 🆕"
        },
        {
          type: "input",
          q: "These shoes are _____. They cost $600!",
          a: "expensive",
          successTip: "SHOPPING ALERT! Expensive = $$$, Cheap = $ ! 💰💸"
        },
        {
          type: "order",
          q: "Traduce: 'Ella tiene un hermoso vestido azul.'",
          words: ["She", "has", "a", "beautiful", "blue", "dress."],
          a: "She has a beautiful blue dress.",
          tip: "Opinion (beautiful) + color (blue) + noun (dress).",
          successTip: "ADJECTIVE ORDER! Opinion BEFORE color! Beautiful blue dress! ✨👗"
        }
      ]
    },

    // ===================================================================
    // SET 3: SIZES (A2)
    // ===================================================================
    {
      id: "m3-u1-set3",
      title: "Set 3: Finding Your Size",
      context: `*Probador de la tienda*

María encuentra un vestido que le gusta. Lo prueba, pero hay un problema...
"Este es muy pequeño," dice. "Necesito una talla más grande."

*Store fitting room*

Maria finds a dress she likes. She tries it on, but there's a problem...
"This is too small," she says. "I need a larger size."`,
      
      questions: [
        {
          type: "match",
          q: "Match the size abbreviation to its meaning:",
          pairs: [
            { left: "XS", right: "Extra Small" },
            { left: "M", right: "Medium" },
            { left: "XL", right: "Extra Large" }
          ],
          a: "completed",
          successTip: "SIZE CODE! XS→M→L→XL. International sizes! 📏🌍"
        },
        {
          type: "mc",
          q: "This shirt is too small for me. I need a _____ size.",
          options: ["larger", "smaller", "tiny"],
          a: "larger",
          tip: "Too small → need LARGER!"
        },
        {
          type: "order",
          q: "Traduce: '¿Qué talla es tu chaqueta?'",
          words: ["What", "size", "is", "your", "jacket?"],
          a: "What size is your jacket?",
          extra: ["The", "black"],
          successTip: "Common shopping question: 'What size...?'"
        },
        {
          type: "mc",
          q: "The sales assistant asks: 'Is small _____ okay for your jacket?'",
          options: ["size", "color", "material"],
          a: "size",
          successTip: "SHOPPING QUESTIONS! 'What size?' = key phrase! 🛍️"
        },
        {
          type: "input",
          q: "I wear _____ gloves. I have very small hands.",
          a: "extra small",
          alts: ["XS", "xs", "extra-small"],
          tip: "Extra small = XS."
        },
        {
          type: "input",
          q: "He needs a _____ shirt. Small is too tight and big is too loose.",
          a: "medium",
          alts: ["M", "m"],
          successTip: "Tight = Ajustado, Loose = Suelto 👕"
        },
        {
          type: "match",
          q: "Match the size to who might wear it:",
          pairs: [
            { left: "Extra Small", right: "Child" },
            { left: "Medium", right: "Average adult" },
            { left: "Extra Large", right: "Tall person" }
          ],
          a: "completed",
          successTip: "SIZE LOGIC! XS=👶 child, M=👨‍🦱 average, XL=🏀 tall!"
        },
        {
          type: "mc",
          q: "This dress is _____. It's Large size.",
          options: ["big", "small", "tiny"],
          a: "big",
          tip: "Large = big size."
        },
        {
          type: "input",
          q: "She has _____ hands, so she needs small gloves.",
          a: "small",
          alts: ["tiny"],
          successTip: "LOGIC MATCH! Small hands → small gloves! 🧤"
        },
        {
          type: "order",
          q: "Traduce: 'Necesito un abrigo grande.'",
          words: ["I", "need", "a", "large", "coat."],
          a: "I need a large coat.",
          extra: ["you","small"],
          successTip: "PERFECT FIT! Article + size + noun! Try it on! 👍"
        }
      ]
    },

    // ===================================================================
    // SET 4: TOO / ENOUGH (A2)
    // ===================================================================
    {
      id: "m3-u1-set4",
      title: "Set 4: Too Tight or Just Right?",
      context: `*Probador - Segundo intento*

María se prueba el vestido en talla Medium.
"Mmm... todavía está muy ajustado. No puedo moverme bien."
Laura le sugiere: "¿Por qué no pruebas talla Large?"

* Fitting room - Second try*

Maria tries on the dress in size Medium.
"Hmm... it's still too tight. I can't move well."
Laura suggests: "Why don't you try size Large?"`,
      
      questions: [
        {
          type: "match",
          q: "Match the problem to the solution:",
          pairs: [
            { left: "Too small", right: "Get bigger size" },
            { left: "Too expensive", right: "Find cheaper item" },
            { left: "Not long enough", right: "Get longer one" }
          ],
          a: "completed",
          successTip: "PROBLEM-SOLUTION! Too = ❌ demasiado, Enough = ✅ suficiente! 🛒"
        },
        {
          type: "mc",
          q: "These pants are too _____. I can't move!",
          options: ["tight", "loose", "comfortable"],
          a: "tight",
          tip: "Too tight = can't move! 😣"
        },
        {
          type: "order",
          q: "Traduce: 'Este vestido es muy largo.'",
          words: ["This", "dress", "is", "too", "long."],
          a: "This dress is too long.",
          extra: ["large","short"],
          tip: "'Large es grande, ancho, suelto y long es largo.'",
          successTip: "Large = grande (big), Long = largo! 🛒"
        },
        {
          type: "mc",
          q: "This coat is not warm _____. I'm still cold!",
          options: ["enough", "too", "very"],
          a: "enough",
          tip: "NOT...ENOUGH! 'Not warm ENOUGH' = insufficient! 🥶",
          successTip: "Still means 'todavía'! I'm STILL cold! 🥶"
        },
        {
          type: "input",
          q: "This skirt is too _____. I want a longer one.",
          a: "short",
          tip: "Too short ≠ long enough."
        },
        {
          type: "input",
          q: "These shoes are not big _____. They hurt my feet.",
          a: "enough",
          tip: "Not big enough = too small! Same problem! 👞"
        },
        {
          type: "match",
          q: "Match the sentence to its meaning:",
          pairs: [
            { left: "Too big", right: "Need smaller size" },
            { left: "Too small", right: "Need bigger size" },
            { left: "Big enough", right: "Perfect size ✓" }
          ],
          a: "completed",
          successTip: "SIZE GUIDE! Too small = not big enough! Same meaning! 📐"
        },
        {
          type: "mc",
          q: "Does the sweater fit? No, it is _____.",
          options: ["too big", "big too", "enough big"],
          a: "too big",
          tip: "Order: too + adjective (not adjective + too).",
          successTip: "Fit = ajuste perfecto!👍 'This shirt fits me'"
        },
        {
          type: "input",
          q: "This jacket is too _____. I can't afford it!($$$$)",
          a: "expensive",
          successTip: "Can't afford = can't buy! 💸😢"
        },
        {
          type: "order",
          q: "Traduce: 'La camisa no es suficientemente barata.'",
          words: ["The", "shirt", "is", "not", "cheap", "enough."],
          a: "The shirt is not cheap enough.",
          extra: ["expensive","shorts"],
          successTip: "GRAMMAR MASTERY! 'Not + adjective + enough' = problem! 🎓"
        }
      ]
    },

    // ===================================================================
    // SET 5: SHOPPING VERBS (A2)
    // ===================================================================
    {
      id: "m3-u1-set5",
      title: "Set 5: At the Checkout",
      context: `*Caja registradora*

María finalmente encuentra el vestido perfecto: talla grande.
"Perfecto, este sí me queda bien. Me lo llevo."
La cajera le sonríe: "Excelente elección. ¿Cómo va a pagar?"

*Checkout counter*

Maria finally finds the perfect dress: size Large.
"Perfect, this fits me well. I'll take it."
The cashier smiles: "Excellent choice. How will you pay?"`,
      
      questions: [
        {
          type: "match",
          q: "Match the verb to its meaning:",
          pairs: [
            { left: "Try on", right: "Put on to test" },
            { left: "Buy", right: "Pay money for" },
            { left: "Choose", right: "Pick one option" }
          ],
          a: "completed",
          successTip: "SHOPPING VERBS! Try on→🧥 test, Buy→💵 pay, Choose→👉 select!"
        },
        {
          type: "mc",
          q: "I always _____ clothes before I buy them.",
          options: ["try on", "sell", "own"],
          a: "try on",
          tip: "Try on = test clothing before buying."
        },
        {
          type: "order",
          q: "Traduce: 'Quiero comprar jeans nuevos.'",
          words: ["I", "want", "to", "buy", "new", "jeans."],
          a: "I want to buy new jeans.",
          extra: ["shoes", "dress"],
          tip: "Want + to + verb."
        },
        {
          type: "mc",
          q: "She _____ a beautiful green dress at the store.",
          options: ["chooses", "choose", "choosing"],
          a: "chooses",
          successTip: "THIRD PERSON! She/he/it → verb+S! She CHOOSES! ✅"
        },
        {
          type: "input",
          q: "They _____ new shoes every year. (comprar)",
          a: "buy",
          tip: "Buy = comprar."
        },
        {
          type: "input",
          q: "This shop _____ very fashionable clothes. (vender)",
          a: "sells",
          alts: ["sell"],
          successTip: "Shop (singular) → sellS."
        },
        {
          type: "match",
          q: "Match the question to the answer:",
          pairs: [
            { left: "Does it fit?", right: "Yes, it's perfect!" },
            { left: "What size?", right: "Medium, please." },
            { left: "How much?", right: "$50" },
             { left: "Does it suit me?", right: "Yes, that color looks great!" }
          ],
          a: "completed",
          successTip: "Cuidado con fit y suit, fit = ajuste perfecto en talla, suit = en estilo 💬🛍️"
        },
        {
          type: "mc",
          q: "I _____ 30 pairs of shoes! I love shopping!",
          options: ["own", "sell", "try"],
          a: "own",
          tip: "Own = possess, have."
        },
        {
          type: "input",
          q: "We _____ for clothes by credit card.",
          a: "pay",
          tip: "Pay = pagar.",
          successTip: "Pay 'BY' credit card 💳 or 'WITH/IN' cash 💵!"
        },
        {
          type: "order",
          q: "Traduce: 'Ella siempre paga en efectivo.'",
          words: ["She", "always", "pays", "with", "cash."],
          a: "She always pays with cash.",
          extra: ["credit card", "debit card"]
        }
      ]
    },

    // ===================================================================
    // SET 6: MATERIALS (A2)
    // ===================================================================
    {
      id: "m3-u1-set6",
      title: "Set 6: What's It Made Of?",
      context: `*Sección de accesorios*

Laura mira bolsos mientras María paga.
"Este bolso de cuero es hermoso," dice Laura. "Pero este de plástico es más barato."
María toca ambos: "El de cuero se siente mejor."

*Accessories section*

Laura looks at bags while Maria pays.
"This leather bag is beautiful," says Laura. "But this plastic one is cheaper."
Maria touches both: "The leather one feels better."`,
      
      questions: [
        {
          type: "match",
          q: "Match the material to what it's made from:",
          pairs: [
            { left: "Leather", right: "Animal skin 🐄" },
            { left: "Wool", right: "Sheep hair 🐑" },
            { left: "Cotton", right: "Plant fibers 🌿" }
          ],
          a: "completed",
          successTip: "MATERIAL ORIGINS! Leather→🐄 animal, Wool→🐑 sheep, Cotton→🌿 plant!"
        },
        {
          type: "mc",
          q: "I have a _____ jacket. It's made from animal skin.",
          options: ["leather", "plastic", "paper"],
          a: "leather",
          tip: "Leather = animal skin material."
        },
        {
          type: "order",
          q: "Traduce: 'Este es un hermoso bolso de cuero.'",
          words: ["This", "is", "a", "beautiful", "leather", "bag."],
          a: "This is a beautiful leather bag.",
          extra: ["plastic", "cotton"],
          tip: "Opinion (beautiful) + material (leather) + noun (bag)."
        },
        {
          type: "mc",
          q: "This sweater is _____. It's very warm!",
          options: ["woolen", "wooden", "plastic"],
          a: "woolen",
          successTip: "Es mas natural decir 'made of wool!' 🧶🐑"
        },
        {
          type: "input",
          q: "These chairs are _____. They are made of wood.",
          a: "wooden",
          tip: "Wood (noun) → wooden (adjective)."
        },
        {
          type: "input",
          q: "I prefer _____ bags instead of paper ones.",
          a: "plastic",
          tip: "Plastic = lightweight and cheap material."
        },
        {
          type: "match",
          q: "Match the item to its common material:",
          pairs: [
            { left: "Bottle", right: "Glass or plastic" },
            { left: "Scarf", right: "Wool or silk" },
            { left: "Table", right: "Wood or metal" }
          ],
          a: "completed",
          successTip: "COMMON MATERIALS! Bottle→🍶 glass, Scarf→🧣 wool! Logical! 🤓"
        },
        {
          type: "mc",
          q: "This beautiful vase is made of _____. Be careful!",
          options: ["glass", "metal", "fabric"],
          a: "glass",
          tip: "Glass = fragile, be careful! 🍷",
          successTip: "Vase = jarrón o florero!🏺"
        },
        {
          type: "input",
          q: "My belt is made of _____. It's made from crocodile skin.",
          a: "leather",
          successTip: "LEATHER BELT! Classic and durable! Goes with everything! 👔✨"
        },
        {
          type: "order",
          q: "Traduce: 'Quiero una mesa de madera.'",
          words: ["I", "want", "a", "wooden", "table."],
          a: "I want a wooden table.",
          successTip: "MATERIAL ADJECTIVES! Wood→wooden, wool→woolen! Add -en! 🪵"
        }
      ]
    },

    // ===================================================================
    // SET 7: OPINION ADJECTIVES (A2+)
    // ===================================================================
    {
      id: "m3-u1-set7",
      title: "Set 7: Beautiful or Horrible?",
      context: `*Cafetería del centro comercial*

María y Laura toman café y hablan de su día de compras.
"¿Te gusta mi vestido?" pregunta María.
"¡Es hermoso! Ese color azul te queda bien," responde Laura.

*Mall café*

Maria and Laura have coffee and talk about their shopping day.
"Do you like my dress?" asks Maria.
"It's beautiful! That blue color suits you well," Laura answers.`,
      
      questions: [
        {
          type: "match",
          q: "Match the opinion to its opposite:",
          pairs: [
            { left: "Beautiful", right: "Ugly" },
            { left: "Lovely", right: "Horrible" },
            { left: "Great", right: "Terrible" }
          ],
          a: "completed",
          successTip: "OPINION OPPOSITES! Beautiful↔Ugly, Lovely↔Horrible! Strong words! 👗👎"
        },
        {
          type: "mc",
          q: "This is a _____ dress! I love the color!",
          options: ["beautiful", "ugly", "boring"],
          a: "beautiful",
          tip: "Beautiful = very pretty, attractive."
        },
        {
          type: "order",
          q: "Traduce: 'Esta es una encantadora bufanda roja.'",
          words: ["This", "is", "a", "lovely", "red", "scarf."],
          a: "This is a lovely red scarf.",
          extra: ["ugly", "blue"],
          tip: "Opinion (lovely) + fact (red) + noun (scarf).",
          successTip: "Siempre usa este orden: Opinion + fact + noun! 🌸"
        },
        {
          type: "mc",
          q: "That's a _____ hat! It's so ugly!",
          options: ["horrible", "lovely", "great"],
          a: "horrible",
          successTip: "HORRIBLE! Very bad/ugly! UK: 'dreadful', US: 'awful'! 😱"
        },
        {
          type: "input",
          q: "This is a _____ party! I'm having a really good time!",
          a: "fun",
          alts: ["great", "lovely"],
          tip: "Fun = enjoyable, entertaining."
        },
        {
          type: "input",
          q: "That movie was _____. I fell asleep!",
          a: "boring",
          tip: "Boring = not interesting, tedious.",
          successTip: "I fell asleep! (me quede dormido) excelente frase para mostrar aburrimiento 🙈"
        },
        {
          type: "match",
          q: "Match the adjective order rule:",
          pairs: [
            { left: "Opinion goes first", right: "Ugly" },
            { left: "Fact, goes after opinion", right: "Blue" },
            { left: "Noun goes at the end", right: "House" }
          ],
          a: "completed",
          successTip: "ADJECTIVE ORDER RULE! Ugly blue house! 📝✅"
        },
        {
          type: "mc",
          q: "I have a _____ new phone. It's the best model!",
          options: ["great", "bad", "horrible"],
          a: "great",
          tip: "Great = excellent, wonderful."
        },
        {
          type: "input",
          q: "This is an _____ sweater! I don't like it.",
          a: "ugly",
          tip: "La palabra debe comenzar con sonido de vocal porque usas 'an'.",
          successTip: "UGLY! Opposite of beautiful! UK: 'hideous' too! 👎😬"
        },
        {
          type: "order",
          q: "Traduce: 'Ella tiene una horrible camisa verde.'",
          words: ["She", "has", "a", "horrible", "green", "shirt."],
          a: "She has a horrible green shirt.",
          extra: ["ugly", "blue"],
          successTip: "PERFECT ORDER! Opinion (horrible) + color (green) + noun! Master! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 8: ADJECTIVE ORDER (A2+/B1-)
    // ===================================================================
    {
      id: "m3-u1-set8",
      title: "Set 8: Getting the Order Right",
      context: `*Conversación en la cafetería*

María describe su compra: "Compre un azul vestido hermoso."
Laura la corrige amablemente: "Te refieres a un hermoso vestido azul!"
María se ríe: "¡Tienes razón! El orden importa."

*Conversation at the café*

Maria describes her purchase: "I bought a blue beautiful dress."
Laura gently corrects her: "You mean a beautiful blue dress!"
Maria laughs: "You're right! Order matters."`,
      
      questions: [
        {
          type: "match",
          q: "Match the correct adjective order:",
          pairs: [
            { left: "Beautiful old house", right: "Correct ✓" },
            { left: "Old beautiful house", right: "Incorrect ✗" },
            { left: "Lovely blue dress", right: "Correct ✓" }
          ],
          a: "completed",
          successTip: "OPINION BEFORE FACT! Beautiful (opinion) OLD (fact)! ✅"
        },
        {
          type: "mc",
          q: "Choose the correct order:",
          options: ["A lovely green hat", "A green lovely hat", "A hat lovely green"],
          a: "A lovely green hat",
          tip: "Opinion (lovely) + color (green) + noun (hat)."
        },
        {
          type: "order",
          q: "Traduce: 'Este es un hermoso bolso de cuero.'",
          words: ["This", "is", "a", "beautiful", "leather", "bag."],
          a: "This is a beautiful leather bag.",
          extra: ["plastic", "cotton"],
          tip: "Opinion (beautiful) + material (leather) + noun (bag)."
        },
        {
          type: "mc",
          q: "Choose the correct order:",
          options: ["An ugly old car", "An old ugly car", "A car old ugly"],
          a: "An ugly old car",
          tip: "age (old) tambien va despues de opinion.",
          successTip: "OPINION + AGE + NOUN! Ugly (opinion) old (fact)! Perfect! 🚗"
        },
        {
          type: "input",
          q: "Complete the correct order: This is a _____ _____ table. (hermosa/de madera)",
          a: "beautiful wooden",
          tip: "Opinion (beautiful) + material (wooden)."
        },
        {
          type: "input",
          q: "Complete the correct order: She has a _____ _____ dress. (corto y rojo)",
          a: "short red",
          alts: ["red short"],
          tip: "Both are fact adjectives; size usually comes first."
        },
        {
          type: "match",
          q: "Match the sentence to correct/incorrect:",
          pairs: [
            { left: "A boring old movie", right: "Correct ✅" },
            { left: "An old boring movie", right: "Incorrect ❌" },
            { left: "A fun new game", right: "Correct ✅" }
          ],
          a: "completed",
          successTip: "REMEMBER! Opinion FIRST! Boring (opinion) OLD (fact)! 📚"
        },
        {
          type: "mc",
          q: "Find the correct sentence:",
          options: ["This is a beautiful green dress", "This is a dress green beautiful", "This is a green beautiful dress"],
          a: "This is a beautiful green dress"
        },
        {
          type: "input",
          q: "Traduce: 'Quiero una encantadora bufanda azul.' → I want a _____ _____ scarf.",
          a: "lovely blue",
          successTip: "LOVELY (opinion) BEFORE BLUE (fact)! Fixed! ✅"
        },
        {
          type: "order",
          q: "Traduce: 'Ellos tienen una casa vieja y horrible.'",
          words: ["They", "have", "a", "horrible","and", "old", "house."],
          a: "They have a horrible and old house.",
          extra: ["beautiful", "but"],
          successTip: "MASTERED! Opinion (horrible) + age (old) + noun! Expert level! 🏆"
        }
      ]
    },

    // ===================================================================
    // SET 9: MIXED PRACTICE (B1-)
    // ===================================================================
    {
      id: "m3-u1-set9",
      title: "Set 9: Real Shopping Situations",
      context: `*De vuelta en las tiendas*

Laura ahora busca zapatos para el trabajo.
En la tienda, practica todo lo que ha aprendido: tallas, colores, materiales...
"Can I try these in medium?" le pregunta a la vendedora.

*Back in the stores*

Laura is now looking for work shoes.
In the store, she practices everything she's learned: sizes, colors, materials...
"Can I try these in medium?" she asks the sales assistant.`,
      
      questions: [
        {
          type: "match",
          q: "Match the situation to the solution:",
          pairs: [
            { left: "Jacket too small", right: "Try larger size" },
            { left: "Dress too expensive", right: "Find cheaper store" },
            { left: "Shoes not comfortable", right: "Try different style" }
          ],
          a: "completed",
          successTip: "REAL PROBLEMS! Too small→larger, too expensive→cheaper! 🛒"
        },
        {
          type: "mc",
          q: "Customer: 'Do you have this in medium?' Clerk: '_____'",
          options: ["Yes, let me check", "I am medium", "Medium is nice"],
          a: "Yes, let me check",
          tip: "Clerk = empleado que atiende.",
          successTip: "Clerk = empleado que atiende, mejor usa sales assistant para vendedores en tiendas de ropa! Always polite! 🛍️"
        },
        {
          type: "order",
          q: "Traduce: '¿Puedo probarme esto?'",
          words: ["Can", "I", "try", "this", "on?"],
          a: "Can I try this on?",
          extra: ["put", "wear"],
          tip: "Common question: 'Can I try this on?'",
          successTip: "TRY THIS ON! A veces los nativos omiten el 'on' pero debe usarse si hablas de vestimenta! 🛍️👗"
        },
        {
          type: "mc",
          q: "This shop _____ beautiful leather bags.",
          options: ["sells", "sell", "selling"],
          a: "sells",
          successTip: "SHOP (singular) → SELLS! Third person! 🏪"
        },
        {
          type: "input",
          q: "I like to _____ new clothes on sale! (comprar)",
          a: "buy",
          tip: "Buy → comprar.",
          successTip: "On sale = en oferta! Great for bargains! 🛒💸"
        },
        {
          type: "input",
          q: "These jeans don't _____ me. They're too tight.",
          a: "fit",
          tip: "Fit = quedar bien (clothing)."
        },
        {
          type: "match",
          q: "Match the payment method:",
          pairs: [
            { left: "by credit card", right: "Visa/Mastercard" },
            { left: "in cash", right: "Bills and coins" },
            { left: "by debit card", right: "Bank account" }
          ],
          a: "completed",
          successTip: "In cash es mas formal aunque with cash es mas comun! 💳💵"
        },
        {
          type: "mc",
          q: "How much _____ this coat?",
          options: ["is", "are", "does"],
          a: "is",
          tip: "Coat (singular) → is."
        },
        {
          type: "input",
          q: "I always _____ by credit card. It's convenient.",
          a: "pay",
          successTip: "PAY BY CARD! Modern shopping! Convenient! 💳✨"
        },
        {
          type: "order",
          q: "Traduce: 'Quiero comprar una chaqueta de cuero nueva.'",
          words: ["I", "want", "to", "buy", "a", "new", "leather", "jacket."],
          a: "I want to buy a new leather jacket.",
          extra: ["put", "wear"],
          successTip: "PERFECT SENTENCE! Opinion + material + noun! Shopping pro! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 10: ADVANCED DESCRIPTIONS (B1-)
    // ===================================================================
    {
      id: "m3-u1-set10",
      title: "Set 10: Fashion Talk",
      context: `*Última tienda del día*

María y Laura miran la sección de ropa formal.
"Para la boda necesito algo elegante," dice María. "No demasiado casual."
Laura está de acuerdo: "Algo elegante pero moderno."

*Last store of the day*

Maria and Laura look at the formal wear section.
"For the wedding I need something smart," says Maria. "Not too casual."
Laura agrees: "Something smart but trendy."`,
      
      questions: [
        {
          type: "match",
          q: "Match the fashion term to its meaning:",
          pairs: [
            { left: "Smart", right: "Formal/elegant" },
            { left: "Casual", right: "Informal/relaxed" },
            { left: "Trendy", right: "Fashionable now" }
          ],
          a: "completed",
          successTip: "FASHION VOCAB! Smart=👔 formal, Casual=👕 relaxed, Trendy=✨ fashionable!"
        },
        {
          type: "mc",
          q: "For the job interview, I wear _____ clothes.",
          options: ["smart", "casual", "dirty"],
          a: "smart",
          tip: "Smart = formal, elegant (UK/US both use this)."
        },
        {
          type: "order",
          q: "Traduce: 'Ella lleva un vestido azul largo hermoso.'",
          words: ["She", "wears", "a", "beautiful", "long", "blue", "dress."],
          a: "She wears a beautiful long blue dress.",
          tip: "Opinion (beautiful) + size (long) + color (blue) + noun."
        },
        {
          type: "mc",
          q: "On weekends, I prefer _____ clothing. Jeans and t-shirts!",
          options: ["casual", "formal", "expensive"],
          a: "casual",
          successTip: "CASUAL! Comfortable, informal! Perfect for weekends! 😎👖"
        },
        {
          type: "input",
          q: "This style is very _____ this year. Everyone wears it!",
          a: "trendy",
          alts: ["fashionable", "popular"],
          tip: "Trendy = in fashion, popular now."
        },
        {
          type: "input",
          q: "I need a _____ and a tie for work. To look professional.",
          a: "suit",
          tip: "Suit = formal outfit (jacket + pants/skirt)."
        },
        {
          type: "match",
          q: "Match the clothing to the occasion:",
          pairs: [
            { left: "Wedding", right: "Smart dress/suit" },
            { left: "Beach", right: "Sandals/shorts" },
            { left: "Office", right: "Formal clothes" }
          ],
          a: "completed",
          successTip: "DRESS FOR THE OCCASION! Wedding=👔 formal, Beach=🏖️ casual!"
        },
        {
          type: "mc",
          q: "At school, students wear a _____. Same clothes for everyone.",
          options: ["uniform", "suit", "dress"],
          a: "uniform",
          tip: "Uniform = same clothes for all students."
        },
        {
          type: "input",
          q: "These boots are made of high-quality _____. (cuero)",
          a: "leather",
          successTip: "HIGH-QUALITY LEATHER! Expensive and durable! Worth it! 👢💎"
        },
        {
          type: "order",
          q: "Traduce: '¡Me encanta comprar ropa nueva!'",
          words: ["I", "love", "shopping", "for", "new", "clothes!"],
          a: "I love shopping for new clothes!",
          successTip: "Shop for = proceso de mirar, comparar, buscar! You can now shop confidently in English! 📖🛍️"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: Shopping Day
  // ===================================================================
  readingSet: {
    id: "m3-u1-reading",
    title: "Reading: A Perfect Shopping Day",
    category: "reading",

    questions: [
      {
        type: "passage",
        passage: `**Saturday Morning**

Today is Saturday, and Maria goes shopping with her friend Laura. They go to a big clothing store in the city center. 

Maria wants to buy a new dress for her cousin's wedding. She tries on a beautiful blue dress, but it's too small. The sales assistant brings her a larger size.`
      },
      {
        type: "mc",
        q: "When does Maria go shopping?",
        options: ["Saturday", "Sunday", "Friday"],
        a: "Saturday",
        tip: "First sentence: 'Today is Saturday...'",
      },
      {
        type: "mc",
        q: "What does Maria want to buy?",
        options: ["A dress", "Shoes", "A coat"],
        a: "A dress",
        successTip: "✓ When you say dress up significa 'Vestirse elegante o... disfrazarse!' 👗"
      },
      {
        type: "input",
        q: "What color is the dress Maria tries on first?",
        a: "blue",
        tip: "The passage says 'beautiful blue dress'.",
        successTip: "✓ BLUE! Classic wedding guest color! 💙"
      },
      {
        type: "mc",
        q: "Why doesn't the first dress fit?",
        options: ["It's too small", "It's too big", "It's too expensive"],
        a: "It's too small",
        successTip: "✓ SIZE PROBLEM! That's why she needs a larger size! 📏"
      },

      {
        type: "passage",
        passage: `**Perfect Fit**

"Perfect!" says Maria. "It fits well!" The dress is made of silk and costs $120. It's a bit expensive, but Maria loves it.`
      },
      {
        type: "input",
        q: "What material is Maria's dress made of?",
        a: "silk",
        tip: "Look for 'made of...' in the passage.",
        successTip: "✓ SILK! Very smooth and high-quality fabric! 💎"
      },
      {
        type: "mc",
        q: "How much does the dress cost?",
        options: ["$120", "$85", "$200"],
        a: "$120",
        tip: "Look at the price mentioned.",
        successTip: "✓ $120! Expensive, but worth it for a wedding! 💵"
      },

      {
        type: "passage",
        passage: `**Laura's Turn**

Laura needs new shoes for work. She wants smart black shoes. She tries on three pairs. The first pair is too tight. The second pair is not comfortable enough. 

The third pair is perfect! They are made of leather and cost $85.`
      },

      {
        type: "input",
        q: "What does Laura need? (Type one word)",
        a: "shoes",
        tip: "Check the first sentence of this block.",
        successTip: "✓ SHOES! Comfortable work shoes are essential! 👠"
      },
      {
        type: "mc",
        q: "How many pairs of shoes does Laura try on?",
        options: ["Three", "Two", "Four"],
        a: "Three",
        successTip: "✓ THREE! Third time's the charm! 3️⃣✨"
      },
      {
        type: "input",
        q: "What material are Laura's shoes made of?",
        a: "leather",
        tip: "The passage says 'made of leather'.",
        successTip: "✓ LEATHER! Durable and professional for work! 👞💼"
      },

      {
        type: "passage",
        passage: `**Coffee Time**

After shopping, they go to a café. "I love shopping!" says Laura. "Me too!" says Maria. "But now I need to save money!"`
      },
      {
        type: "mc",
        q: "What do they do after shopping?",
        options: ["Go to a café", "Go home", "Buy more clothes"],
        a: "Go to a café",
        tip: "The last paragraph tells you their final destination.",
        successTip: "✓ CAFÉ TIME! Coffee after shopping = perfect ending! ☕🎉"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m3-u1-review",
    title: "Unit 1 Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match clothing to where you wear it:",
        pairs: [
          { left: "Hat", right: "Head" },
          { left: "Gloves", right: "Hands" },
          { left: "Shoes", right: "Feet" }
        ],
        a: "completed",
        successTip: "✓ BASICS! You remember clothing locations! 👒🧤👟"
      },
      {
        type: "mc",
        q: "What color is the sky?",
        options: ["Blue", "Green", "Red"],
        a: "Blue",
        tip: "Visual memory: sky = blue ☁️"
      },
      {
        type: "input",
        q: "This shirt is too tight. I need a _____ size.",
        a: "larger",
        alts: ["bigger", "large", "big"],
        successTip: "✓ SIZE LOGIC! Too small → need larger! 📏"
      },
      {
        type: "mc",
        q: "This jacket is _____ expensive. I can't buy it.",
        options: ["too", "enough", "very"],
        a: "too",
        tip: "Too + adjective = problem."
      },
      {
        type: "input",
        q: "I always _____ _____ clothes before buying them.",
        a: "try on",
        alts: ["try"],
        tip: "Try on = test clothing."
      },
      {
        type: "order",
        q: "Traduce: 'Este es un hermoso vestido rojo.'",
        words: ["This", "is", "a", "beautiful", "red", "dress."],
        a: "This is a beautiful red dress.",
        tip: "Opinion (beautiful) + color (red) + noun (dress)."
      },
      {
        type: "mc",
        q: "This sweater is _____. It's made from sheep!",
        options: ["woolen", "wooden", "plastic"],
        a: "woolen",
        successTip: "✓ WOOLEN! Made of wool from sheep! 🐑🧶"
      },
      {
        type: "input",
        q: "Completa la frase: 'Tengo una hermosa bufanda verde.' → I have a _____ _____ scarf.",
        a: "lovely green",
        tip: "Opinion before color!"
      },
      {
        type: "mc",
        q: "For work, I wear _____ clothes. Suit and tie.",
        options: ["smart", "casual", "dirty"],
        a: "smart",
        tip: "Smart = formal, elegant."
      },
      {
        type: "order",
        q: "Traduce: '¡Me encanta comprar ropa nueva!'",
        words: ["I", "love", "buying", "new", "clothes!"],
        a: "I love buying new clothes!",
        tip: "Love + gerund (buying).",
        successTip: "🏆 UNIT 1 COMPLETE! You mastered clothing vocabulary, colors, sizes, adjective order, and shopping! Ready for Unit 2! 🎉"
      }
    ]
  }
};
